import { type JSX } from 'react';

import { Box, Skeleton, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

const SHIMMER_ANIMATION = 'pulse' as const;

const HISTORY_ROW_HEIGHT = 48;
const HISTORY_ROW_COUNT = 6;
const FORM_INPUT_HEIGHT = 40;
const FORM_INPUT_COUNT = 4;
const DETAIL_LINE_HEIGHT = 14;
const DETAIL_LINE_COUNT = 6;

export type ModalContentSkeletonVariant = 'form' | 'detail' | 'history';

export interface ModalContentSkeletonProps {
  variant?: ModalContentSkeletonVariant;
  ariaLabel?: string;
}

/**
 * Skeleton placeholder for a dialog body. Variant tailors the shape so
 * the swap to real content is shift-free:
 *   - `form`    — title + 4 input-height rectangulars + button row.
 *   - `detail`  — title + 2 paragraphs (3 lines each).
 *   - `history` — title + 6 history-row rectangulars (matches AuditHistoryModal).
 */
export const ModalContentSkeleton = ({
  variant = 'detail',
  ariaLabel,
}: ModalContentSkeletonProps): JSX.Element => {
  const { t } = useTranslation();
  return (
    <Box
      role="status"
      aria-busy="true"
      aria-label={ariaLabel ?? t('loading')}
      data-testid={`modal-skeleton-${variant}`}
    >
      <Skeleton
        variant="text"
        animation={SHIMMER_ANIMATION}
        sx={{ fontSize: 18, width: '40%', mb: 2 }}
      />
      {variant === 'form' ? <FormSkeleton /> : null}
      {variant === 'detail' ? <DetailSkeleton /> : null}
      {variant === 'history' ? <HistorySkeleton /> : null}
    </Box>
  );
};

const FormSkeleton = (): JSX.Element => (
  <Stack spacing={1.5}>
    {Array.from({ length: FORM_INPUT_COUNT }, (_, i) => (
      <Skeleton
        key={`form-skel-${i}`}
        variant="rectangular"
        animation={SHIMMER_ANIMATION}
        height={FORM_INPUT_HEIGHT}
        sx={{ borderRadius: 1 }}
      />
    ))}
    <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'flex-end' }}>
      <Skeleton variant="rectangular" animation={SHIMMER_ANIMATION} width={88} height={36} sx={{ borderRadius: 1 }} />
      <Skeleton variant="rectangular" animation={SHIMMER_ANIMATION} width={104} height={36} sx={{ borderRadius: 1 }} />
    </Box>
  </Stack>
);

const DetailSkeleton = (): JSX.Element => (
  <Stack spacing={0.75}>
    {Array.from({ length: DETAIL_LINE_COUNT }, (_, i) => (
      <Skeleton
        key={`detail-skel-${i}`}
        variant="text"
        animation={SHIMMER_ANIMATION}
        width={`${85 - (i % 3) * 10}%`}
        sx={{ fontSize: DETAIL_LINE_HEIGHT }}
      />
    ))}
  </Stack>
);

const HistorySkeleton = (): JSX.Element => (
  <Stack spacing={1}>
    {Array.from({ length: HISTORY_ROW_COUNT }, (_, i) => (
      <Skeleton
        key={`history-skel-${i}`}
        variant="rectangular"
        animation={SHIMMER_ANIMATION}
        height={HISTORY_ROW_HEIGHT}
        sx={{ borderRadius: 1 }}
      />
    ))}
  </Stack>
);
